import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, parse, differenceInDays } from "date-fns";
import { useInfiniteFunds } from "./funds/fund";
import { useClientsInfiniteQuery } from "./client";
import { getLocationInfo } from "../api/metadata";
import {
  downloadReport,
  generateReportData,
  GenerateReportDataPayload,
  getCalendarDate,
  ReportType,
  initiatePdfGeneration,
  checkPdfStatus,
  downloadPdf,
  generateDistributorReport,
  fetchStates,
  fetchCountries,
  getMinStartDate,
  getPlanMinStartDate,
} from "../api/common";
import { titleCase } from "../utils";
import { PlanFrequency } from "../types";

export const useInView = (
  target: React.RefObject<HTMLElement | null>,
  options?: IntersectionObserverInit
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (target.current) {
      const callback = (entries: IntersectionObserverEntry[]) => {
        setIsIntersecting(entries[0].isIntersecting);
      };

      observer = new IntersectionObserver(callback, options);
      observer.observe(target.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, [target, options]);

  return isIntersecting;
};

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const useGlobalSearch = (search: string, enabled: boolean = false) => {
  const fundsQuery = useInfiniteFunds(
    { pageNo: 1, pageSize: 5, search },
    { maxPages: 0, enabled }
  );
  const clientsQuery = useClientsInfiniteQuery(search, undefined, undefined, {
    enabled,
    pageSize: 5,
  });

  const funds = useMemo(
    () => fundsQuery.data?.pages.flatMap((page) => page.results),
    [fundsQuery.data]
  );

  return {
    data: {
      funds,
      clients: clientsQuery.clients,
    },
    isLoading: fundsQuery.isLoading || clientsQuery.isLoading,
    isError: fundsQuery.isError || clientsQuery.isError,
    isPending: fundsQuery.isPending || clientsQuery.isPending,
    isPlaceholderData:
      fundsQuery.isPlaceholderData || clientsQuery.isPlaceholderData,
    isFetchingNextPage:
      fundsQuery.isFetchingNextPage || clientsQuery.isFetchingNextPage,
    fetchNextFundsPage: fundsQuery.fetchNextPage,
    fetchNextClientsPage: clientsQuery.fetchNextPage,
    hasNextFundsPage: fundsQuery.hasNextPage,
    hasNextClientsPage: clientsQuery.hasNextPage,
  };
};

export const useLocationInfo = (pincode: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ["location-info", pincode],
    queryFn: () => getLocationInfo(pincode),
    enabled,
  });
};

export const useExpiry = (seconds: number) => {
  const [expired, setExpired] = useState(true);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const start = useCallback(() => {
    if (timer.current) {
      return;
    }
    setExpired(false);
    timer.current = setTimeout(() => {
      setExpired(true);
      timer.current = null;
    }, seconds * 1000);
  }, [seconds]);

  const forceExpire = useCallback(() => {
    setExpired(true);
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  return { expired, start, forceExpire };
};

export const useDownloadReport = () => {
  return useMutation({
    mutationFn: (url: string) => downloadReport(url),
  });
};

// New async PDF generation hooks
export const useInitiatePdfGeneration = () => {
  return useMutation({
    mutationFn: (payload: {
      url: string;
      dimensions?: { width: number; height: number };
    }) => initiatePdfGeneration(payload.url, payload.dimensions),
  });
};

export const useCheckPdfStatus = () => {
  return useMutation({
    mutationFn: (taskId: string) => checkPdfStatus(taskId),
  });
};

export const useDownloadPdf = () => {
  return useMutation({
    mutationFn: (taskId: string) => downloadPdf(taskId),
  });
};

export async function webDownload(blob: Blob, fileName?: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "portfolio.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

export const generateReportJsonOptions = <T extends ReportType>(
  payload: Omit<GenerateReportDataPayload<T>, "file_type">
) =>
  queryOptions({
    queryKey: ["generate-report-data", payload],
    queryFn: () =>
      generateReportData<T, "json">({
        ...payload,
        file_type: "json",
      } as GenerateReportDataPayload<T>),
  });

export const useGenerateReportJson = <T extends ReportType>(
  payload: GenerateReportDataPayload<T>
) => {
  return useQuery(generateReportJsonOptions<T>(payload));
};

export const useGenerateReportFile = <T extends ReportType>() => {
  return useMutation({
    mutationFn: (payload: GenerateReportDataPayload<T>) =>
      generateReportData<T, "pdf" | "excel" | "csv" | "json">(payload),
  });
};

export const useNextWorkingDay = (date: string, workingDays: string) => {
  return useQuery({
    queryKey: ["next-working-day", date, workingDays],
    queryFn: () => getCalendarDate({ date, workingDays }),
  });
};

export const useMinStartDate = (
  frequency: "DAILY" | "WEEKLY" | "MONTHLY" = "MONTHLY",
  firstOrderToday: boolean = false
) => {
  return useQuery({
    queryKey: ["min-start-date", frequency, firstOrderToday],
    queryFn: () =>
      getMinStartDate({ frequency, first_order_today: firstOrderToday }),
  });
};

export const usePlanMinStartDate = (
  variables: Parameters<typeof getPlanMinStartDate>[0],
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      "plan-min-start-date",
      {
        planType: variables.planType,
        firstOrderToday: variables.firstOrderToday,
        frequency: variables.frequency,
      },
    ],
    queryFn: () => getPlanMinStartDate(variables),
    ...options,
  });
};

export const useStartDate = () => {
  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const fourDaysFromToday = useMemo(
    () => format(new Date().setDate(new Date().getDate() + 4), "yyyy-MM-dd"),
    []
  );

  const { data } = useNextWorkingDay(todayStr, "1");

  const nextWorkingDayStr = data?.result_date ?? fourDaysFromToday;

  return useCallback(
    (value: string | number, firstOrderToday: boolean) => {
      const day = Number(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextWorkingDate = parse(
        nextWorkingDayStr,
        "yyyy-MM-dd",
        new Date()
      );
      const differenceInDaysRequired = firstOrderToday ? 7 : 4;
      const targetDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        day,
        0,
        0,
        0,
        0
      );
      let diff = differenceInDays(targetDate, nextWorkingDate);
      while (diff < 0 || diff < differenceInDaysRequired) {
        targetDate.setMonth(targetDate.getMonth() + 1);
        diff = differenceInDays(targetDate, nextWorkingDate);
      }
      return format(targetDate, "yyyy-MM-dd");
    },
    [nextWorkingDayStr]
  );
};

export const useStartDateV2 = (
  frequency: PlanFrequency = "MONTHLY",
  firstOrderToday: boolean = false
) => {
  const { data } = useMinStartDate(frequency, firstOrderToday);

  /**
   * @value min start date object based on frequency and firstOrderToday
   */
  const minDate = useMemo(
    () =>
      data?.min_start_date
        ? parse(data.min_start_date, "dd/MM/yyyy", new Date())
        : null,
    [data?.min_start_date]
  );

  /**
   * @param value - date in yyyy-MM-dd format
   */
  const getStartDate = useCallback(
    (value: string) => {
      const startDate = parse(value, "yyyy-MM-dd", new Date());
      startDate.setHours(0, 0, 0, 0);
      if (!minDate) return format(startDate, "yyyy-MM-dd");
      while (differenceInDays(startDate, minDate) < 0) {
        if (frequency === "MONTHLY") {
          startDate.setMonth(startDate.getMonth() + 1);
        } else if (frequency === "DAILY") {
          startDate.setDate(startDate.getDate() + 1);
        } else if (frequency === "WEEKLY") {
          startDate.setDate(startDate.getDate() + 7);
        } else {
          break;
        }
      }
      return format(startDate, "yyyy-MM-dd");
    },
    [minDate, frequency]
  );

  return useMemo(
    () => [minDate, getStartDate] as const,
    [minDate, getStartDate]
  );
};

export const usePlanStartDate = (
  variables: Parameters<typeof usePlanMinStartDate>[0],
  options?: { enabled?: boolean }
) => {
  const { frequency } = variables;

  const { data } = usePlanMinStartDate(variables, options);

  /**
   * @value min start date object based on frequency and firstOrderToday
   */
  const minDate = useMemo(
    () =>
      data?.min_start_date
        ? parse(data.min_start_date, "dd/MM/yyyy", new Date())
        : null,
    [data?.min_start_date]
  );

  /**
   * @param value - date in yyyy-MM-dd format
   */
  const getStartDate = useCallback(
    (value: string) => {
      const startDate = parse(value, "yyyy-MM-dd", new Date());
      startDate.setHours(0, 0, 0, 0);
      if (!minDate) return format(startDate, "yyyy-MM-dd");
      while (differenceInDays(startDate, minDate) < 0) {
        if (frequency === "MONTHLY") {
          startDate.setMonth(startDate.getMonth() + 1);
        } else if (frequency === "DAILY") {
          startDate.setDate(startDate.getDate() + 1);
        } else if (frequency === "WEEKLY") {
          startDate.setDate(startDate.getDate() + 7);
        } else {
          break;
        }
      }
      return format(startDate, "yyyy-MM-dd");
    },
    [minDate, frequency]
  );

  return useMemo(
    () => [minDate, getStartDate] as const,
    [minDate, getStartDate]
  );
};

export function useGenerateDistributorReport() {
  return useMutation({
    mutationKey: ["distributor-report"],
    mutationFn: generateDistributorReport,
  });
}

export function useStates() {
  return useQuery({
    queryKey: ["states"],
    queryFn: fetchStates,
    staleTime: Infinity,
  });
}

export function useCountries() {
  return useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
    staleTime: Infinity,
  });
}

export function useCountryOptions() {
  const { data, error, isLoading } = useCountries();

  if (error || isLoading) return [];

  return (
    data?.map((s) => {
      return {
        label: titleCase(s.details),
        value: s.code,
      };
    }) || []
  );
}

export function useStateOptions() {
  const { data, error, isLoading } = useStates();

  if (error || isLoading) return [];

  return (
    data?.map((s) => {
      return {
        label: titleCase(s.state_name),
        value: titleCase(s.state_name),
      };
    }) || []
  );
}

export { generateReportData } from "../api/common";
